import React from "react";
import ReactDOM from "react-dom/client";
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from "./authConfig";
import App from "./App";
import { Provider } from "react-redux";
import { store } from "./shared";

/**
 * MSAL should be instantiated outside of the component tree to prevent re-instantiating on re-renders.
 */
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Function to initialize MSAL before rendering the app.
 */
const initializeMsal = async () => {
  try {
    // Ensure MSAL is initialized before proceeding
    await msalInstance.initialize();

    // Default to using the first account if no active account exists
    if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }

    // Listen for authentication events
    msalInstance.addEventCallback((event: EventMessage) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          event.eventType === EventType.SSO_SILENT_SUCCESS) &&
        event.payload &&
        "account" in event.payload
      ) {
        const authResult = event.payload as AuthenticationResult;
        msalInstance.setActiveAccount(authResult.account);
      }
    });

    // Render the React app only after MSAL is initialized
    const rootElement = document.getElementById("root");
    if (!rootElement) {
      throw new Error("Failed to find the root element");
    }

    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <Provider store={store}>
          <MsalProvider instance={msalInstance}>
            <App />
          </MsalProvider>
        </Provider>
      </React.StrictMode>
    );
  } catch (error) {
    console.error("MSAL Initialization Failed:", error);
  }
};

// Start the initialization process
initializeMsal();
